import AJAX from 'common/AJAX';
import { CommentErrorMessages } from 'common/constants/comments';
import useMutation, { type MutationOptions } from 'common/hooks/useMutation';

import { useIdeasContext } from '../containers/IdeasContainer';

import type { QueryParams } from 'common/actions/ideaActivityQueries';
import type {
  CommentResponse,
  CreateIdeaCommentRequest,
  DeleteIdeaCommentRequest,
  UpdateIdeaCommentRequest,
} from 'common/api/endpoints/ideaActivity';

export const useCreateIdeaComment = (
  ideaQueryParams: QueryParams,
  options?: MutationOptions<CommentResponse, CreateIdeaCommentRequest>,
) => {
  const { upsertIdeaComments } = useIdeasContext();

  return useMutation<CommentResponse, CreateIdeaCommentRequest>({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/ideas/comments/create', data);
      return response;
    },
    ...options,
    errors: CommentErrorMessages,
    isResponseSuccessful: (response) => !!response.comment,
    onSuccess: (response, data) => {
      upsertIdeaComments(ideaQueryParams, [response.comment]);

      options?.onSuccess?.(response, data);
    },
  });
};

export const useUpdateIdeaComment = (
  ideaQueryParams: QueryParams,
  options?: MutationOptions<CommentResponse, UpdateIdeaCommentRequest>,
) => {
  const { upsertIdeaComments } = useIdeasContext();

  return useMutation<CommentResponse, UpdateIdeaCommentRequest>({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/ideas/comments/update', data);
      return response;
    },
    ...options,
    errors: CommentErrorMessages,
    isResponseSuccessful: (response) => !!response.comment,
    onSuccess: (response, data) => {
      upsertIdeaComments(ideaQueryParams, [response.comment]);

      options?.onSuccess?.(response, data);
    },
  });
};

export const useDeleteIdeaComment = (
  ideaQueryParams: QueryParams,
  options?: MutationOptions<void, DeleteIdeaCommentRequest>,
) => {
  const { removeIdeaComments } = useIdeasContext();

  return useMutation<void, DeleteIdeaCommentRequest>({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/ideas/comments/delete', data);
      return response;
    },
    ...options,
    errors: CommentErrorMessages,
    onSuccess: (response, data) => {
      removeIdeaComments(ideaQueryParams, [data.commentID]);

      options?.onSuccess?.(response, data);
    },
  });
};
